import { API, Card, Help, Input, Select, ViewHeader, useSubmit } from '@enkla-gui'
import React, { useContext, useEffect, useState } from 'react'
import { date, notify, cashmoneyhelper } from '@/_helpers'

import { UserContext } from '@/App'
import './membership.css'

export default function Membership() {
  const [loading, setLoading] = useState(true)
  const [membership, setMembership] = useState(false)
  const { user, billable, setBillable } = useContext(UserContext)
  const [showInvoiceForm, setShowInvoiceForm] = useState(false)
  const [manualCancelSent, setManualCancelSent] = useState(false)

  const invoiceRequest = useSubmit({
    endpoint: 'api/membership/request-invoice-payment',
    success: () => {
      setShowInvoiceForm(false)
      API.get('api/auth/billable').then(b => setBillable(b))
      refetch()
    }
  })

  const refetch = () => {
    setLoading(true)
    API.get('api/membership/subscription', {}).then(res => {
      setMembership(res)
      setLoading(false)
    }).catch(() => {
      setMembership(false)
      setLoading(false)
    })
  }

  const cancelSubscription = async () => {
    if (!(await notify.confirm('Vill du avsluta ditt abonnemang?'))) return
    setLoading(true)
    API.post('api/membership/cancel', {}).then(() => refetch())
  }

  const cancelManualSubscription = async () => {
    if (!(await notify.confirm('Vill du avsluta ditt abonnemang?'))) return
    setLoading(true)
    API.post('api/membership/cancel-manual', {}).then(() => {
      setManualCancelSent(true)
      setLoading(false)
    })
  }

  const renewSubscription = () => {
    setLoading(true)
    API.post('api/membership/resume', {}).then(() => refetch())
      .catch(([err, res]) => {
        notify.toast('Något gick fel. Se till att du har lagt till en betalningsmetod och prova igen senare.', 'error')
        setLoading(false)
      })
  }

  useEffect(() => {
    refetch()
  }, [])

  // ── Data ───────────────────────────────────────────────────────

  const cardInfo = membership?.payment_methods?.find(pm => pm?.card)
  const cardDisplay = cardInfo
    ? `${cardInfo.card.brand.charAt(0).toUpperCase() + cardInfo.card.brand.slice(1)} ••••${cardInfo.card.last4}`
    : null

  const trialEnd = membership?.current_period_end
    ? date.short(membership.current_period_end)
    : ''

  const daysLeft = membership?.current_period_end
    ? Math.max(0, Math.ceil((membership.current_period_end * 1000 - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0

  const upcomingAmount = membership?.upcoming_invoice?.total
    ? cashmoneyhelper.format(membership.upcoming_invoice.total / 100)
    : null

  const stripe = {
    isTrialNoCard: membership.is_on_trial && membership.is_active_member && !membership.has_payment_method && !membership.is_canceled,
    isTrialHasCard: membership.is_on_trial && membership.is_active_member && membership.has_payment_method && !membership.is_canceled,
    isPastDueWithCard: membership.is_past_due && membership.has_payment_method,
    isPastDueNoCard: membership.is_past_due && !membership.has_payment_method,
    isUserCanceled: membership.is_canceled && (membership.has_payment_method || membership.is_on_trial),
    isCanceledNoCard: membership.is_canceled && !membership.has_payment_method && !membership.is_on_trial,
    isActive: membership.is_paying_member && membership.is_active_member && !membership.is_canceled && !membership.is_past_due,
    hasNoMembership: !membership.is_active_member && !membership.is_paying_member && !membership.is_canceled,
  }

  const showPaymentCards = stripe.isTrialNoCard || stripe.hasNoMembership || stripe.isPastDueNoCard || stripe.isCanceledNoCard

  // ── Shared layout wrapper ────────────────────────────────────

  const Page = ({ children }) => (
    <div class='membership-page'>
      <ViewHeader loading={!membership} icon='fa-money-check-alt' title='Inställningar' subtitle='Abonnemang' />
      {children}
    </div>
  )

  const CancelInfo = () => (
    <div class='membership-cancel-info'>
      <i class='fas fa-info-circle' />
      <span>Ingen uppsägningstid eller bindningstid. Vid uppsägning har du tillgång till systemet under resterande tid av din innevarande period</span>
    </div>
  )

  const PricingPanel = () => !showInvoiceForm && showPaymentCards ? (
    <><div class='pricing-panel'>
      <div class='pricing-panel__grid'>
        <div class='pricing-panel__left'>
          <div class='pricing-panel__badge'>Enkel prissättning &ndash; inga dolda avgifter</div>

          <div class='pricing-panel__price'>
            <span class='pricing-panel__amount'>kr 399</span>
            <span class='pricing-panel__period'>/mån</span>
          </div>
          <div class='pricing-panel__vat'>inkl. moms för privatpersoner, exkl. moms för företag</div>

          <div class='pricing-panel__features'>
            <div class='pricing-panel__feature'><i class='fas fa-check pricing-panel__check' />Obegränsat antal fastigheter &amp; objekt</div>
            <div class='pricing-panel__feature'><i class='fas fa-check pricing-panel__check' />Automatisk hyresavisering &amp; påminnelser</div>
            <div class='pricing-panel__feature'><i class='fas fa-check pricing-panel__check' />Hyresgästportal med felanmälan</div>
            <div class='pricing-panel__feature'><i class='fas fa-check pricing-panel__check' />Integrationer med Fortnox, Visma &amp; mer</div>
            <div class='pricing-panel__feature'><i class='fas fa-check pricing-panel__check' />Digital signering via Zigned</div>
            <div class='pricing-panel__feature'><i class='fas fa-check pricing-panel__check' />Löpande uppdateringar &amp; support</div>
          </div>

          <div class='pricing-panel__no-binding'>Ingen bindningstid &ndash; ingen uppsägningstid</div>
        </div>

        <div class='pricing-panel__right'>
          <div class='pricing-panel__method pricing-panel__method--primary'>
            <div class='pricing-panel__method-icon'><i class='fas fa-credit-card' /></div>
<div class='pricing-panel__method-title'>Betala med kort</div>
<div class='pricing-panel__method-desc'>Vi använder Stripe för säker hantering av kortbetalningar.</div>
              <div class='pricing-panel__method-action'>
                <a class='btn btn-primary' href={membership.url} target='_blank' rel='noopener noreferrer'>
                  <i class='fas fa-wallet me-2' />Lägg till betalmetod
                </a>
              </div>
            </div>
          <div class='pricing-panel__method pricing-panel__method--info'>
            <div class='pricing-panel__method-icon'><i class='fas fa-file-invoice' /></div>
            <div class='pricing-panel__method-title'>Betala mot faktura</div>
            <div class='pricing-panel__method-desc'>Faktura skickas enligt önskat intervall.</div>
            <div class='pricing-panel__method-action'>
              <button class='btn btn-outline-info' onClick={() => setShowInvoiceForm(true)} disabled={loading}>
                <i class='fas fa-file-invoice me-2' />Fyll i uppgifter
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class='pricing-panel__reload-hint'><i class='fas fa-sync-alt' />Har du redan kopplat ditt kort? Prova att ladda om sidan.</div>
  </>) : null

  const InvoiceForm = () => showInvoiceForm ? (
    <Card title='Bli fakturakund' icon='fas fa-file-invoice' loading={loading}>
      <form onSubmit={invoiceRequest.submit}>
        <div class='row'>
          <Input controls={invoiceRequest} name='company_name' label='Företagsnamn' options={{ required: true }} error={invoiceRequest.error} cols='col-md-6' />
          <Input controls={invoiceRequest} name='org_number' label='Organisationsnummer' options={{ required: true }} error={invoiceRequest.error} cols='col-md-6' />
        </div>
        <div class='row'>
          <Input controls={invoiceRequest} name='address' label='Adress' options={{ required: true }} error={invoiceRequest.error} cols='col-12' />
        </div>
        <div class='row'>
          <Input controls={invoiceRequest} name='postal_code' label='Postnummer' options={{ required: true }} error={invoiceRequest.error} cols='col-md-4' />
          <Input controls={invoiceRequest} name='city' label='Ort' options={{ required: true }} error={invoiceRequest.error} cols='col-md-8' />
        </div>
        <div class='row'>
          <Input controls={invoiceRequest} name='reference' label='Fakturareferens' options={{ required: true }} error={invoiceRequest.error} cols='col-md-6' />
          <Input controls={invoiceRequest} name='email' label='E-post för fakturor' type='email' options={{ required: true }} error={invoiceRequest.error} cols='col-md-6' />
        </div>
        <div class='row mb-3'>
          <Select controls={invoiceRequest} name='interval' label='Betalningsintervall' options={{ required: true }} error={invoiceRequest.error} cols='col-md-6' values={[{ key: '1 månad', value: '1 månad' }, { key: '12 månader', value: '12 månader' }]} />
          <Input controls={invoiceRequest} name='phone' label='Telefonnummer' placeholder='Valfritt' error={invoiceRequest.error} cols='col-md-6' />
        </div>
        <Help type='info' header={false}>
          <p>När du skickar in en begäran om att bli fakturakund kommer ditt abonnemang att aktiveras direkt.</p>
          <p class='mb-0'>Det innebär att du automatiskt blir medlem och att betalning via faktura aktiveras när din testperiod är slut.</p>
          <p class='mb-0 mt-2'>Genom att skicka in din begäran godkänner du att vi påbörjar fakturering i samband med att din provperiod avslutas.</p>
        </Help>
        <div class='d-flex gap-2 mt-3'>
          <button type='submit' class='btn btn-primary' disabled={invoiceRequest.loading}>
            {invoiceRequest.loading ? <><i class='fas fa-circle-notch fa-spin me-2' />Skickar...</> : <><i class='fas fa-paper-plane me-2' />Bli kund</>}
          </button>
          <button type='button' class='btn btn-secondary text-white' onClick={() => setShowInvoiceForm(false)}>Avbryt</button>
        </div>
      </form>
    </Card>
  ) : null

  // ── Status card component (active / canceled / past-due) ─────

  const StatusCard = ({ type }) => {
    const groups = {
      active: {
        iconClass: 'membership-status-card__icon--active',
        icon: 'fa-check-circle',
        title: 'Aktivt abonnemang',
        info: (
          <div class='membership-info-grid'>
            <div class='membership-info-grid__item'>
              <span class='membership-info-grid__label'><i class='fas fa-money-check-alt me-1' />Betaltyp</span>
              <span class='membership-info-grid__value'>Kortbetalning via Stripe</span>
            </div>
            <div class='membership-info-grid__item'>
              <span class='membership-info-grid__label'><i class='fas fa-credit-card me-1' />Kort</span>
              <span class='membership-info-grid__value'>{cardDisplay || 'Kort registrerat'}</span>
            </div>
            <div class='membership-info-grid__item'>
              <span class='membership-info-grid__label'><i class='fas fa-calendar-alt me-1' />Nästa debitering</span>
              <span class='membership-info-grid__value'>{upcomingAmount ? `${upcomingAmount} • ${trialEnd}` : trialEnd}</span>
            </div>
          </div>
        ),
        actions: (
          <>
            <a class='btn btn-secondary' href={membership.url} target='_blank' rel='noopener noreferrer'>
              <i class='fas fa-external-link-alt me-2' />Gå till Stripes portal
            </a>
            <button class='membership-status-card__btn-cancel' onClick={cancelSubscription} disabled={loading}>
              <i class='fas fa-times me-2' />Avsluta abonnemang
            </button>
          </>
        ),
        footer: <CancelInfo />,
      },
      canceled: {
        iconClass: 'membership-status-card__icon--warning',
        icon: 'fa-exclamation-circle',
        title: 'Abonnemanget har avslutats',
        info: (
          <>
            <div class='membership-status-card__info'>
              <p>Du har fortsatt tillgång till systemet under den tid du redan har betalat för. Om du i framtiden väljer att återaktivera abonnemanget fortsätter du där du slutade.</p>
            </div>
            <div class='membership-info-grid'>
              <div class='membership-info-grid__item'>
                <span class='membership-info-grid__label'><i class='fas fa-hourglass-half me-1' />Tillgång till</span>
                <span class='membership-info-grid__value'>{trialEnd} ({daysLeft} dagar kvar)</span>
              </div>
            </div>
          </>
        ),
        actions: (
          <>
            <button class='btn btn-primary' onClick={renewSubscription} disabled={loading}>
              <i class='fas fa-redo me-2' />Återaktivera abonnemang
            </button>
            <a class='btn btn-secondary' href={membership.url} target='_blank' rel='noopener noreferrer'>
              <i class='fas fa-external-link-alt me-2' />Gå till Stripes portal
            </a>
          </>
        ),
      },
      'past-due': {
        iconClass: 'membership-status-card__icon--danger',
        icon: 'fa-exclamation-triangle',
        title: 'Betalningen misslyckades',
        info: (
          <>
            <div class='membership-status-card__info'>
              <p>Din senaste betalning kunde inte genomföras. Det kan bero på att kortet har gått ut, blivit spärrat eller att betalningen av annan anledning nekades.</p>
              <p class='mb-0'>Behöver du mer hjälp kan du alltid <a href='mailto:kontakt@enklafastigheter.se'>kontakta oss</a>.</p>
            </div>
            <div class='membership-payment__reload-hint'>
              <i class='fas fa-sync-alt' />Har du redan uppdaterat ditt kort i Stripe? Prova att ladda om sidan.
            </div>
            <div class='membership-info-grid'>
              <div class='membership-info-grid__item'>
                <span class='membership-info-grid__label'><i class='fas fa-credit-card me-1' />Kort</span>
                <span class='membership-info-grid__value'>{cardDisplay || 'Kort registrerat'}</span>
              </div>
            </div>
          </>
        ),
        actions: (
          <a class='btn btn-primary' href={membership.url} target='_blank' rel='noopener noreferrer'>
            <i class='fas fa-wallet me-2' />Gå till Stripes betalportal
          </a>
        ),
      },
    }

    const g = groups[type]

    return (
      <Card loading={loading}>
        <div class='membership-status-card'>
          <div class={`membership-status-card__icon ${g.iconClass}`}>
            <i class={`fas ${g.icon}`} />
          </div>
          <div class='membership-status-card__title'>{g.title}</div>
          <div class='membership-status-card__body'>
            {g.info}
            <div class='membership-card-actions'>{g.actions}</div>
          </div>
        </div>
      </Card>
    )
  }

  // ── Scenario views ───────────────────────────────────────────

  const renderManualSub = () => {
    if (manualCancelSent) {
      return (
        <Page>
          <Help type='info' icon='fas fa-info-circle' title='Uppsägning mottagen' loading={loading}>
            <p>Vi har tagit emot din begäran om att avsluta abonnemanget.</p>
            <p class='mb-0'>Ditt abonnemang förblir aktivt till dess att innevarande betalperiod är slut. Du kommer inte att debiteras för någon ny period.</p>
          </Help>
        </Page>
      )
    }

    return (
      <Page>
        {membership?.is_on_trial && (
          <Help type='primary' icon='fas fa-clock' title='Kostnadsfri testperiod' loading={loading}>
            <p>Du har <b>{daysLeft} dagar</b> kvar på din testperiod (avslutas <b>{trialEnd}</b>). När testperioden är slut skickar vi en faktura till dig.</p>
          </Help>
        )}
        <Card loading={loading}>
          <div class='membership-status-card'>
            <div class='membership-status-card__icon membership-status-card__icon--active'>
              <i class='fas fa-check-circle' />
            </div>
            <div class='membership-status-card__title'>Aktivt abonnemang</div>
            <div class='membership-status-card__body'>
              <div class='membership-info-grid'>
                <div class='membership-info-grid__item'>
                  <span class='membership-info-grid__label'><i class='fas fa-building me-1' />Abonnemangstyp</span>
                  <span class='membership-info-grid__value'>Fakturakund</span>
                </div>
              </div>
              <div class='membership-card-actions'>
                <button class='membership-status-card__btn-cancel' onClick={cancelManualSubscription} disabled={loading}>
                  <i class='fas fa-times me-2' />Avsluta abonnemang
                </button>
              </div>
            </div>
          </div>
        </Card>
        <CancelInfo />
      </Page>
    )
  }

  const renderActive = () => (
    <>
      {stripe.isTrialHasCard && (
        <Help type='primary' icon='fas fa-clock' title='Kostnadsfri testperiod' loading={loading}>
          <p>Ditt kort är registrerat men ingen debitering sker förrän testperioden är slut. Du har <b>{daysLeft} dagar</b> kvar ({trialEnd}).</p>
        </Help>
      )}
      <StatusCard type='active' />
    </>
  )

  const renderTrialNoCard = () => (
    <>
      <Help type='primary' icon='fas fa-rocket' title='Kostnadsfri testperiod' loading={loading}>

        <p>Du har <b>{daysLeft} dagar</b> kvar på din testperiod (avslutas <b>{trialEnd}</b>).</p>
     <p>
        Gillar du tjänsten? Du kan redan nu välja att starta ett abonnemang. Du
        behåller hela din återstående testperiod och vi börjar inte fakturera förrän
        testperioden är slut.
      </p>

      </Help>
      <PricingPanel />
      <InvoiceForm />
    </>
  )

  const renderCanceled = () => (
    <StatusCard type='canceled' />
  )

  const renderPastDue = () => (
    <StatusCard type='past-due' />
  )

  const renderNoPaymentMethod = () => (
    <>
      <Help type='warning' icon='fas fa-credit-card' title='Inget aktivt abonnemang'>
        <p>För att fortsätta använda systemet behöver du ett aktivt abonnemang. Välj en betalningsmetod nedan för att komma igång.</p>
      </Help>
      <PricingPanel />
      <InvoiceForm />
    </>
  )

  const renderNoMembership = () => (
    <>
      <Help type='warning' icon='fas fa-hourglass-end' title='Testperioden är avslutad' loading={loading}>
        <p>Din kostnadsfria testperiod har löpt ut. Välj en betalningsmetod nedan för att aktivera ditt abonnemang.</p>
      </Help>
      <PricingPanel />
      <InvoiceForm />
    </>
  )

  // ── Main render ──────────────────────────────────────────────

  if (billable.manual_sub) {
    return renderManualSub()
  }

  return (
    <Page>
      <div hidden={!membership}>
        {stripe.isPastDueWithCard && renderPastDue()}
        {(stripe.isPastDueNoCard || stripe.isCanceledNoCard) && renderNoPaymentMethod()}
        {stripe.isUserCanceled && renderCanceled()}
        {stripe.isTrialNoCard && renderTrialNoCard()}
        {stripe.isTrialHasCard && renderActive()}
        {stripe.isActive && renderActive()}
        {stripe.hasNoMembership && renderNoMembership()}
      </div>
    </Page>
  )
}
